import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useTheme } from '../../contexts/ThemeContext';
import SalesHistory from './SalesHistory';
import ManageOffers from './ManageOffers';
import SalesHistoryPage from './SalesHistoryPage';
import SharedHeader from './SharedHeader';
import { AllCustomerShopping, getProfileUpdate, AllProfile, PutBusinessProfile, ApprovalBusiness, getApprovalBusiness, getClaimRequests, updateClaimRequest, createCustomerTransaction } from '../../services/api';
import { 
  Store, Settings, Users, Gift, TrendingUp, DollarSign, 
  Award, Target, Calendar, Plus, Edit, Percent, History,
  Eye, LogOut, ArrowLeft, Menu, X, Bell, Search, Filter,
  ChevronDown, BarChart3, ShoppingCart, CreditCard, Star,
  Receipt, ShoppingBag, User, QrCode, Scan
} from 'lucide-react';
import { toast } from 'react-hot-toast';
import QRScanner from './QRScanner';
import QRGenerator from './QRGenerator';

const BusinessDashboard = () => {
  const { currentUser, logout } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedCard, setSelectedCard] = useState(null);
  const [showCardDetails, setShowCardDetails] = useState(false);
  const [showDropdownMenu, setShowDropdownMenu] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showProfileModal, setShowProfileModal] = useState(false);
  // View mode for Available Points card: 'customers' | 'topups'
  const [availablePointsView, setAvailablePointsView] = useState('customers');
  const [showEditBusinessModal, setShowEditBusinessModal] = useState(false);
  const [showPatchSettingsModal, setShowPatchSettingsModal] = useState(false);
  const [showShopDetailsModal, setShowShopDetailsModal] = useState(false);
  const [showCreateOfferModal, setShowCreateOfferModal] = useState(false);
  const [showRewardConfigModal, setShowRewardConfigModal] = useState(false);
  const [showManageOffersModal, setShowManageOffersModal] = useState(false);
  const [showCustomerManagementModal, setShowCustomerManagementModal] = useState(false);
  const [showBusinessAnalyticsModal, setShowBusinessAnalyticsModal] = useState(false);
  const [showViewAllActivityModal, setShowViewAllActivityModal] = useState(false);
  const [showOfferReceiptModal, setShowOfferReceiptModal] = useState(false);
  const [showBuyPointsModal, setShowBuyPointsModal] = useState(false);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [showQRGenerator, setShowQRGenerator] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [showCreditByMobileModal, setShowCreditByMobileModal] = useState(false);
  const [creditMobile, setCreditMobile] = useState('');
  const [creditAmount, setCreditAmount] = useState('');
  const [creditLoading, setCreditLoading] = useState(false);
  const [dateFilter, setDateFilter] = useState('all'); // 'all', 'today', 'lastWeek', 'lastMonth', 'custom'
  const [customDateRange, setCustomDateRange] = useState({ start: '', end: '' });
  
  // Pagination and sorting states
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(5);
  const [sortField, setSortField] = useState('');
  const [sortDirection, setSortDirection] = useState('asc');
  
  // API data states
  const [customerData, setCustomerData] = useState([]);
  const [cardData, setCardData] = useState([]);
  const [profileData, setProfileData] = useState(null);
  const [allCustomers, setAllCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showManageOffers, setShowManageOffers] = useState(false);
  const [showSalesHistory, setShowSalesHistory] = useState(false);
  // Approvals (buy points) for this business
  const [approvalRequests, setApprovalRequests] = useState([]);
  const [approvalLoading, setApprovalLoading] = useState(false);
  const [approvedPointsSum, setApprovedPointsSum] = useState(0);
  const [approvedAmountSum, setApprovedAmountSum] = useState(0);
  const [pendingPointsSum, setPendingPointsSum] = useState(0);
  const hasPendingApprovals = approvalRequests.some(a => a.status === 'pending');


const [points, setPoints] = useState('');
  const [amount, setAmount] = useState('');

  // Claim requests from customers for this business
  const [claimRequests, setClaimRequests] = useState([]);
  const [claimLoading, setClaimLoading] = useState(false);
  // Claim approve modal state
  const [showApproveModal, setShowApproveModal] = useState(false);
  const [approveTarget, setApproveTarget] = useState(null); // selected claim row
  const [approveAmount, setApproveAmount] = useState('');
  const [approvedClaimPointsSum, setApprovedClaimPointsSum] = useState(0);
  // Add Transaction modal state
  const [showAddTxnModal, setShowAddTxnModal] = useState(false);
  const [addTxnQuery, setAddTxnQuery] = useState('');
  const [addTxnSelected, setAddTxnSelected] = useState(null);
  const [addTxnAmount, setAddTxnAmount] = useState('');
  const [addTxnLoading, setAddTxnLoading] = useState(false);
  const [useCustomerPoints, setUseCustomerPoints] = useState(false);
  const [redeemPoints, setRedeemPoints] = useState('');
  const [salesRefreshTick, setSalesRefreshTick] = useState(0);

  const RATE_PER_POINT = 10; // ₹10 per point
  // OTP state for redemption verification
  const [otpSent, setOtpSent] = useState(false);
  const [otpCode, setOtpCode] = useState('');
  const [otpInput, setOtpInput] = useState('');
  const [otpVerified, setOtpVerified] = useState(false);

  const handlePointsChange = (e) => {
    const pts = e.target.value;
    setPoints(pts);
    setAmount(pts ? (pts * RATE_PER_POINT).toLocaleString() : '');
  };

  // Recompute table data when date filter or source data changes
  useEffect(() => {
    try {
      if (showCardDetails && selectedCard) {
        const next = generateCardData(selectedCard);
        setCardData(next);
      }
    } catch (e) {
      console.warn('Failed to regenerate card data for filter change', e);
    }
  }, [dateFilter, customDateRange.start, customDateRange.end, customerData, selectedCard, showCardDetails]);

  // Unique customers (for Add Transaction autocomplete)
  const uniqueCustomers = useMemo(() => {
    const map = new Map();
    (customerData || []).forEach(it => {
      const email = it.email_id || '';
      if (!email) return;
      if (!map.has(email)) {
        map.set(email, {
          email,
          name: it.name || email.split('@')[0] || 'Customer',
          mobile: it.mobile || ''
        });
      }
    });
    return Array.from(map.values());
  }, [customerData]);

  // const filteredCustomerOptions = useMemo(() => {
  //   const q = addTxnQuery.trim().toLowerCase();
  //   if (!q) return uniqueCustomers.slice(0, 10);
  //   return uniqueCustomers
  //     .filter(c => (c.name || '').toLowerCase().includes(q)
  //       || (c.email || '')?.toLowerCase().includes(q)
  //       || (c.mobile || '')?.toLowerCase().includes(q))
  //     .slice(0, 10);
  // }, [uniqueCustomers, addTxnQuery]);
  const filteredCustomerOptions = useMemo(() => {
    const q = addTxnQuery.trim().toLowerCase();
    if (!q) return uniqueCustomers.slice(0, 10);
  
    return uniqueCustomers
      .filter(c => 
        String(c.name || '').toLowerCase().includes(q) ||
        String(c.email || '').toLowerCase().includes(q) ||
        String(c.mobile || '').toLowerCase().includes(q)
      )
      .slice(0, 10);
  }, [uniqueCustomers, addTxnQuery]);
  
  // Helper: parse currency/number string to number
  const parseAmountToNumber = (val) => {
    if (val == null) return null;
    if (typeof val === 'number') return isNaN(val) ? null : val;
    if (typeof val === 'string') {
      const num = parseFloat(val.replace(/[₹,\s]/g, ''));
      return isNaN(num) ? null : num;
    }
    return null;
  };

  // Helper: extract possible amount field from a record
  const extractAmount = (rec) => {
    if (!rec) return null;
    return (
      rec.amount ??
      rec.purchase_amount ??
      rec.purchaseAmount ??
      rec.purchase ??
      rec.total ??
      null
    );
  };

  // Helper: find latest transaction amount for a customer by email
  const getLatestAmountForEmail = (email) => {
    if (!email || !Array.isArray(customerData)) return null;
    const emailKey = (x) => x.email_id || x.email || x.customer_email;
    const dateKey = (x) => x.transaction_date || x.date || x.createdAt || x.txn_date;
    const items = customerData.filter(x => emailKey(x) === email);
    if (!items.length) return null;
    items.sort((a, b) => new Date(dateKey(b) || 0) - new Date(dateKey(a) || 0));
    const amt = extractAmount(items[0]);
    const num = parseAmountToNumber(amt);
    return num;
  };

  // When selecting a customer in Add Transaction modal, prefill amount from latest txn if available
  const selectAddTxnCustomer = (opt) => {
    setAddTxnSelected(opt);
    setAddTxnQuery(`${opt.name} (${opt.email}${opt.mobile ? `, ${opt.mobile}` : ''})`);
  };
  

  // Fetch fresh profile for current user (used before opening modals)
  const fetchLatestProfileForCurrentUser = async () => {
    const profileResponse = await getProfileUpdate({});
    if (profileResponse?.extractedData) {
      const raw = Array.isArray(profileResponse.extractedData)
        ? profileResponse.extractedData
        : (profileResponse.extractedData?.records || profileResponse.extractedData || []);
      let chosen = Array.isArray(raw)
        ? raw.find(p => (p.profile_id === currentUser?.uid) || (p._id === currentUser?.uid) || (p.email === currentUser?.email))
        : raw;
      if (!chosen && Array.isArray(raw) && raw.length) chosen = raw[0];
      if (chosen) {
        setProfileData(chosen);

        // Auto-sync identity fields from auth: email, displayName -> first/last, shopName
        try {
          const docId = chosen?._id || chosen?.id || chosen?.profile_id;
          if (!docId || !currentUser) return;

          const updates = {};
          // Email
          if (currentUser.email && chosen.email !== currentUser.email) {
            updates.email = currentUser.email;
          }

          // First/Last from displayName if missing (prefer stored value over auth)
          const dn = sessionStorage.getItem('displayName') || localStorage.getItem('displayName') || currentUser.displayName || '';
          if (dn && (!chosen.firstName || !chosen.lastName)) {
            const parts = dn.trim().split(/\s+/);
            if (!chosen.firstName && parts[0]) updates.firstName = parts[0];
            if (!chosen.lastName && parts.slice(1).join(' ')) updates.lastName = parts.slice(1).join(' ');
          }

          // Shop name from displayName if shop exists and shopName missing
          let shops = Array.isArray(chosen.shopDetails) ? JSON.parse(JSON.stringify(chosen.shopDetails)) : [];
          if (!shops[0]) shops[0] = {};
          if (dn && !shops[0].shopName) {
            shops[0].shopName = dn;
          }

          const payload = { profile_id: currentUser.uid, ...updates };
          if (JSON.stringify(shops) !== JSON.stringify(chosen.shopDetails)) {
            payload.shopDetails = shops;
          }

          if (Object.keys(payload).length > 1 /* beyond profile_id */) {
            await PutBusinessProfile(docId, payload);
            // reflect locally
            setProfileData(prev => ({ ...(prev || {}), ...updates, shopDetails: payload.shopDetails || prev.shopDetails }));
          }
        } catch (e) {
          console.warn('Auto-sync identity skipped:', e);
        }
      }
    }
  };

  // Refresh approvals only (used by poller)
  const refreshApprovals = async () => {
    try {
      const myEmail = (currentUser?.email || '').toLowerCase();
      if (!myEmail) return;
      const approvalsResp = await getApprovalBusiness({});
      const approvalsList = Array.isArray(approvalsResp.extractedData) ? approvalsResp.extractedData : (approvalsResp.extractedData?.records || []);
      const mine = approvalsList
        .filter(a => (a.businessEmail || a.email || '').toLowerCase() === myEmail)
        .map((it, idx) => ({
          id: it._id || it.id || String(idx),
          name: it.Name || it.businessName || '',
          email: it.businessEmail || it.email || '',
          points: Number(it.points ?? it.point ?? 0),
          amount: Number(it.amount ?? 0),
          status: (it.status || 'pending').toLowerCase(),
          requestDate: it.requestDate || it.created_on || new Date().toISOString(),
        }));
      setApprovalRequests(mine);
      const approvedOnly = mine.filter(x => x.status === 'approved');
      const ptsSum = approvedOnly.reduce((s, x) => s + (Number(x.points)||0), 0);
      const amtSum = approvedOnly.reduce((s, x) => s + (Number(x.amount)||0), 0);
      setApprovedPointsSum(ptsSum);
      setApprovedAmountSum(amtSum);
      const pendingOnly = mine.filter(x => x.status === 'pending');
      const pendingPts = pendingOnly.reduce((s, x) => s + (Number(x.points)||0), 0);
      setPendingPointsSum(pendingPts);
    } catch (e) {
      // Silent failure to avoid UI noise during background polling
      console.warn('Approvals refresh failed', e?.message || e);
    }
  };

// Auto-process pending customer purchase claims for this business (no manual approval)
const autoProcessPendingPurchases = async () => {
  try {
    const myEmail = (currentUser?.email || '').toLowerCase();
    if (!myEmail) return;

    const claimsResp = await getClaimRequests({});
    const list = Array.isArray(claimsResp.extractedData)
      ? claimsResp.extractedData
      : (claimsResp.extractedData?.records || []);

    const pendingMine = (list || [])
      .filter(c =>
        ((c.businessEmail || c.email || '').toLowerCase() === myEmail) &&
        ((c.status || 'pending').toLowerCase() === 'pending')
      )
      .map((it) => ({
        raw: it,
        id: it._id || it.id || null,
        customerEmail: it.customerEmail || it.email_id || '',
        customerName: it.customerName || it.name || (it.customerEmail ? it.customerEmail.split('@')[0] : 'Customer'),
        amount: Number(it.amount || 0),
      }))
      .filter(x => !!x.id);

    console.log('[autoProcess] found pending for me:', pendingMine.length);

    for (const c of pendingMine) {
      try {
        const points = computePointsForAmount(c.amount); // 0.1 pts per ₹
        await createCustomerTransaction({
          email_id: c.customerEmail,
          name: c.customerName,
          amount: c.amount,
          point_given: points,
          transaction_date: new Date().toISOString(),
          payment_method: 'Auto',
          status: 'Completed',
        });
        await updateClaimRequest(c.id, {
          status: 'approved',
          amount: c.amount,
          approvedDate: new Date().toISOString(),
        });
        console.log('[autoProcess] approved claim', c.id, 'points:', points);
      } catch (inner) {
        console.warn('[autoProcess] single claim failed', c.id, inner?.message || inner);
      }
    }

    if (pendingMine.length) {
      await fetchBusinessData();
    }
  } catch (e) {
    console.warn('[autoProcess] failed', e?.message || e);
  }
};

// Handlers for Add Transaction (Total Sales)
const handleAddTxnApprove = async () => {
  try {
    if (!addTxnSelected?.email) {
      toast.error('Select a customer');
      return;
    }
    const amt = Number(addTxnAmount);
    if (!amt || amt <= 0) {
      toast.error('Enter a valid amount');
      return;
    }
    const computedPts = computePointsForAmount(amt);
    const redeem = useCustomerPoints ? Number(redeemPoints || 0) : 0;
    if (useCustomerPoints) {
      if (!redeem || redeem <= 0) {
        toast.error('Enter redeem points');
        return;
      }
      // const maxRedeem = Math.floor(amt / RATE_PER_POINT);
      // if (redeem > maxRedeem) {
      //   toast.error(`Redeem points cannot exceed ${maxRedeem} for this amount`);
      //   return;
      // }
      if (!otpVerified) {
        toast.error('Verify OTP to proceed');
        return;
      }
      // Optional: enforce a cap using available balance if needed
    }
    setAddTxnLoading(true);
    const points = computedPts;
    const netPoints = points - (useCustomerPoints ? redeem : 0);
    // Single create with net points and metadata
    await createCustomerTransaction({
      email_id: addTxnSelected.email,
      name: addTxnSelected.name || addTxnSelected.email.split('@')[0],
      amount: amt,
      credited_points: netPoints,
      // metadata for transparency/auditing
      purchase_amount: amt,
      point_given: points,
      redeem_points: useCustomerPoints ? redeem : 0,
      amount_redeem_value: useCustomerPoints ? redeem * RATE_PER_POINT : 0,
      otp_code: useCustomerPoints ? otpCode : undefined,
      transaction_date: new Date().toISOString(),
      // Mark payment method clearly when redemption is used
      payment_method: useCustomerPoints ? 'Points+Cash' : 'Cash',
      // Optional: include calculated payable amount for reference
      payable_amount: Math.max(0, amt - (useCustomerPoints ? redeem : 0)),
      status: 'Completed',
    });
    toast.success(`Saved. Credited ${points} pts${useCustomerPoints ? `, Redeemed ${redeem} pts` : ''}`);
    setShowAddTxnModal(false);
    setAddTxnQuery('');
    setAddTxnSelected(null);
    setAddTxnAmount('');
    setUseCustomerPoints(false);
    setRedeemPoints('');
    setOtpSent(false);
    setOtpCode('');
    setOtpInput('');
    setOtpVerified(false);
    await fetchBusinessData();
    // Notify SalesHistoryPage to refresh its data
    setSalesRefreshTick(prev => prev + 1);
  } catch (e) {
    console.error('Add txn failed', e);
    toast.error('Failed to add transaction');
  } finally {
    setAddTxnLoading(false);
  }
};

const handleAddTxnReject = () => {
  setShowAddTxnModal(false);
  setAddTxnQuery('');
  setAddTxnSelected(null);
  setAddTxnAmount('');
};



  // Inline patch helper for shopDetails fields
  const patchShopField = async (fieldPath, value, shopIndex = 0) => {
    try {
      // Ensure we have the chosen profile doc id
      const docId = profileData?._id || profileData?.id || profileData?.profile_id;
      if (!docId) {
        toast.error('Profile not loaded');
        return;
      }
      const shops = Array.isArray(profileData?.shopDetails) ? JSON.parse(JSON.stringify(profileData.shopDetails)) : [];
      if (!shops[shopIndex]) shops[shopIndex] = {};

      // Support nested paths like 'location.lat'
      const parts = String(fieldPath).split('.');
      let cursor = shops[shopIndex];
      for (let i = 0; i < parts.length - 1; i++) {
        const k = parts[i];
        if (typeof cursor[k] !== 'object' || cursor[k] === null) cursor[k] = {};
        cursor = cursor[k];
      }
      cursor[parts[parts.length - 1]] = value;

      await PutBusinessProfile(docId, { shopDetails: shops });
      // Update local state to reflect change
      setProfileData(prev => ({ ...(prev || {}), shopDetails: shops }));
      toast.success('Saved');
    } catch (err) {
      console.error('Patch shop field failed:', err);
      toast.error('Save failed');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const pts = Number(points || 0);
    const email = currentUser?.email || '';
    const Name = currentUser?.displayName || '';

    if (!email || !pts) {
      toast.error('Enter valid points and ensure you are logged in');
      return;
    }
    const amtValue = pts * RATE_PER_POINT; // keep numeric for API
    try {
      await ApprovalBusiness({
        Name: Name,
        businessEmail: email,
        points: pts,
        amount: amtValue,
        status: 'pending',
        requestDate: new Date().toISOString(),
      });
      toast.success(`Point purchase request sent! ${pts} points for ₹${amtValue.toLocaleString()}`);

      // Optimistically add to approvals table and pending totals
      const optimistic = {
        id: `local_${Date.now()}`,
        name: Name,
        email: email,
        points: pts,
        amount: amtValue,
        status: 'pending',
        requestDate: new Date().toISOString(),
      };
      setApprovalRequests(prev => [optimistic, ...(prev || [])]);
      setPendingPointsSum(prev => (Number(prev) || 0) + pts);
    } catch (err) {
      console.error('ApprovalBusiness (buy points) failed', err);
      toast.error('Failed to send point purchase request');
    } finally {
      setShowBuyPointsModal(false);
      setPoints('');
      setAmount('');
    }
  };

  // Fetch data from APIs
  useEffect(() => {
    fetchBusinessData();
    // Re-run when currentUser identifiers become available
  }, [currentUser?.email, currentUser?.uid]);

  // Auto-refresh approvals while there are pending requests so approved ones reflect immediately
  useEffect(() => {
    let intervalId;
    const start = async () => {
      if (!currentUser?.email) return;
      if (!hasPendingApprovals) return;
      // initial refresh
      await refreshApprovals();
      intervalId = setInterval(refreshApprovals, 10000); // every 10s
    };
    start();
    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [currentUser?.email, hasPendingApprovals]);

  const fetchBusinessData = async () => {
    try {
      setLoading(true);
      
      // Fetch customer shopping data for dashboard stats
      const customerResponse = await AllCustomerShopping({});
      if (customerResponse.extractedData) {
        setCustomerData(customerResponse.extractedData);
      }

      // Fetch profile data for shop details and offers
      const profileResponse = await getProfileUpdate({});
      if (profileResponse?.extractedData) {
        const raw = Array.isArray(profileResponse.extractedData)
          ? profileResponse.extractedData
          : (profileResponse.extractedData?.records || profileResponse.extractedData || []);
        let chosen = Array.isArray(raw)
          ? raw.find(p => (p.profile_id === currentUser?.uid) || (p._id === currentUser?.uid) || (p.email === currentUser?.email))
          : raw;
        // Fallback to first profile if specific match not found
        if (!chosen && Array.isArray(raw) && raw.length) chosen = raw[0];
        if (chosen) setProfileData(chosen);
      }

      // Fetch all customers for customer management
      const allCustomersResponse = await AllProfile({});
      if (allCustomersResponse.extractedData) {
        setAllCustomers(allCustomersResponse.extractedData);
      }

      // Fetch my point approval requests
      try {
        setApprovalLoading(true);
        // Only attempt if we have a business email
        const myEmail = (currentUser?.email || '').toLowerCase();
        if (!myEmail) {
          setApprovalRequests([]);
        } else {
          const approvalsResp = await getApprovalBusiness({});
          const approvalsList = Array.isArray(approvalsResp.extractedData) ? approvalsResp.extractedData : (approvalsResp.extractedData?.records || []);
          const mine = approvalsList
            .filter(a => (a.businessEmail || a.email || '').toLowerCase() === myEmail)
            .map((it, idx) => ({
              id: it._id || it.id || String(idx),
              name: it.Name || it.businessName || '',
              email: it.businessEmail || it.email || '',
              points: Number(it.points ?? it.point ?? 0),
              amount: Number(it.amount ?? 0),
              status: (it.status || 'pending').toLowerCase(),
              requestDate: it.requestDate || it.created_on || new Date().toISOString(),
            }));
          setApprovalRequests(mine);
          const approvedOnly = mine.filter(x => x.status === 'approved');
          const ptsSum = approvedOnly.reduce((s, x) => s + (Number(x.points)||0), 0);
          const amtSum = approvedOnly.reduce((s, x) => s + (Number(x.amount)||0), 0);
          setApprovedPointsSum(ptsSum);
          setApprovedAmountSum(amtSum);
          const pendingOnly = mine.filter(x => x.status === 'pending');
          const pendingPts = pendingOnly.reduce((s, x) => s + (Number(x.points)||0), 0);
          setPendingPointsSum(pendingPts);
        }
      } finally {
        setApprovalLoading(false);
      }

      // Fetch customer claim requests for this business
      try {
        setClaimLoading(true);
        const myEmail = (currentUser?.email || '').toLowerCase();
        if (!myEmail) {
          setClaimRequests([]);
        } else {
          const claimsResp = await getClaimRequests({});
          const list = Array.isArray(claimsResp.extractedData) ? claimsResp.extractedData : (claimsResp.extractedData?.records || []);
          const mine = (list || [])
            .filter(c => (c.businessEmail || c.email || '').toLowerCase() === myEmail)
            .map((it, idx) => ({
              id: it._id || it.id || String(idx),
              customerEmail: it.customerEmail || it.email_id || '',
              customerName: it.customerName || it.name || '',
              businessEmail: it.businessEmail || it.email || myEmail,
              businessName: it.businessName || '',
              amount: Number(it.amount || 0),
              billImage: it.billImage || '',
              status: (it.status || 'pending').toLowerCase(),
              requestDate: it.requestDate || it.created_on || new Date().toISOString(),
            }));
          setClaimRequests(mine);
          // Compute sum of points already approved via claims, to reduce business total points
          const approvedPts = mine
            .filter(r => r.status === 'approved')
            .reduce((s, r) => s + computePointsForAmount(r.amount), 0);
          setApprovedClaimPointsSum(approvedPts);
        }
      } catch (e) {
        console.warn('Claim requests fetch failed', e?.message || e);
      } finally {
        setClaimLoading(false);
      }

    } catch (error) {
      console.error('Error fetching business data:', error);
      toast.error('Failed to load business data');
    } finally {
      setLoading(false);
    }
  };

  // Calculate business stats from API data
  const calculateBusinessStats = () => {
    if (!customerData.length) {
      return {
        pointsGiven: 0,
        availablePoints: 0,
        totalPurchases: 0,
        monthlyIncome: 0,
        totalCustomers: 0,
        activeOffers: 0,
        conversionRate: 0,
        avgOrderValue: 0
      };
    }

    const totalPurchases = customerData.length;
    console.log("totalPurchases",customerData)
    const totalIncome = customerData.reduce((sum, customer) => {
      // Handle the actual API response structure
      const amount = customer.amount || 0;
      return sum + (typeof amount === 'number' ? amount : parseFloat(amount) || 0);
    }, 0);
    
    const totalPoints = customerData.reduce((sum, customer) => {
      // Use point_given from the API response - ensure we get the exact value
      const points = parseInt(customer.point_given) || parseInt(customer.points) || 0;
      return sum + points;
    }, 0);
    
    console.log('Points calculation debug:', {
      totalCustomers: customerData.length,
      totalPoints,
      sampleData: customerData.slice(0, 3).map(c => ({ 
        point_given: c.point_given, 
        points: c.points,
        amount: c.amount ,
        mobile:c.mobile
      }))
    });

    return {
      pointsGiven: totalPoints,
      availablePoints: Math.floor(totalPoints * 0.6), // Assume 60% still available
      totalPurchases,
      monthlyIncome: totalIncome,
      totalCustomers: new Set(customerData.map(c => c.email_id)).size, // Unique customers
      activeOffers: profileData?.totalOffers || 0,
      conversionRate: totalPurchases > 0 ? ((totalPurchases / (totalPurchases + 50)) * 100).toFixed(1) : 0,
      avgOrderValue: totalPurchases > 0 ? Math.floor(totalIncome / totalPurchases) : 0
    };
  };

  const businessStats = calculateBusinessStats();

  const businessConfig = {
    businessName:
      profileData?.shopDetails?.[0]?.shopName ||
      [profileData?.firstName, profileData?.lastName].filter(Boolean).join(' ') ||
      currentUser?.displayName ||
      'My Business',
    businessEmail: profileData?.email || currentUser?.email || 'business@example.com',
    businessPhone: profileData?.phone || '+91 98765 43210',
    businessAddress: profileData?.address || '123 Business Street, City, State 12345',
    fixedDiscount: Number(profileData?.shopDetails?.[0]?.specialDiscount) || 10,
    pointsPerRupee: 0.1,
    minRedemptionPoints: 100
  };

  // Filter sales history based on date filter
  const getFilteredSalesHistory = () => {
    let filtered = [...salesHistory];
    
    if (dateFilter === 'today') {
      const today = new Date().toDateString();
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date).toDateString();
        return itemDate === today;
      });
    } else if (dateFilter === 'lastWeek') {
    const now = new Date();
    const sevenDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
    filtered = filtered.filter(item => {
      const itemDate = new Date(item.date);
      return itemDate >= sevenDaysAgo && itemDate <= now;
    });
  } else if (dateFilter === 'lastMonth') {
    const now = new Date();
    const thirtyDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
    filtered = filtered.filter(item => {
      const itemDate = new Date(item.date);
      return itemDate >= thirtyDaysAgo && itemDate <= now;
    });
  } else if (dateFilter === 'thisMonth') {
      const now = new Date();
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date);
        return itemDate >= startOfMonth && itemDate <= endOfMonth;
      });
    } else if (dateFilter === 'custom' && customDateRange.start && customDateRange.end) {
      const startDate = new Date(customDateRange.start);
      const endDate = new Date(customDateRange.end);
      filtered = filtered.filter(item => {
        const itemDate = new Date(item.date);
        return itemDate >= startDate && itemDate <= endDate;
      });
    }
    
    return filtered;
  };

  // Generic date filter helper for arrays based on current dateFilter/customDateRange
  const filterByDateRange = (items, getDateFn) => {
    if (!Array.isArray(items)) return [];
    const now = new Date();
    if (dateFilter === 'today') {
      const today = now.toDateString();
      return items.filter(it => new Date(getDateFn(it)).toDateString() === today);
    }
    if (dateFilter === 'lastWeek') {
      const sevenDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 7);
      return items.filter(it => {
        const d = new Date(getDateFn(it));
        return d >= sevenDaysAgo && d <= now;
      });
    }
    if (dateFilter === 'lastMonth') {
      const thirtyDaysAgo = new Date(now.getFullYear(), now.getMonth(), now.getDate() - 30);
      return items.filter(it => {
        const d = new Date(getDateFn(it));
        return d >= thirtyDaysAgo && d <= now;
      });
    }
    if (dateFilter === 'thisMonth') {
      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      return items.filter(it => {
        const d = new Date(getDateFn(it));
        return d >= startOfMonth && d <= endOfMonth;
      });
    }
    if (dateFilter === 'custom' && customDateRange.start && customDateRange.end) {
      const startDate = new Date(customDateRange.start);
      const endDate = new Date(customDateRange.end);
      return items.filter(it => {
        const d = new Date(getDateFn(it));
        return d >= startDate && d <= endDate;
      });
    }
    return items;
  };

  // Generate card data from API responses
  const generateCardData = (cardType) => {
    if (!customerData || customerData.length === 0) return [];
    
    switch (cardType) {
      case 'pointsGiven':
        return filterByDateRange(
          customerData.filter(item => (item.point_given || 0) > 0),
          it => it.transaction_date || it.created_on
        )
          .map(item => ({
            id: item.id || item._id,
            customer: item.name || item.email_id?.split('@')[0] || 'Customer',
            email: item.email_id,
            date: item.transaction_date || item.created_on,
            points: item.point_given,
            amount: item.amount,
            mobile: item.mobile,
            type: 'Points Given'
          }));
          
      case 'availablePoints':
        // Calculate available points per customer (after date filtering on transactions)
        const customerPoints = {};
        const filteredTxns = filterByDateRange(customerData, it => it.transaction_date || it.created_on);
        filteredTxns.forEach(item => {
          const email = item.email_id;
          if (!customerPoints[email]) {
            customerPoints[email] = {
              customer: item.name || email?.split('@')[0] || 'Customer',
              email: email,
              totalEarned: 0,
              totalRedeemed: 0,
              mobile: item.mobile,
              amount: item.amount,
              pointBalance: item.point_balance || 0,
              date: item.transaction_date || item.created_on,
            };
          }
          customerPoints[email].totalEarned += item.point_given || 0;
          customerPoints[email].pointBalance = item.point_balance || 0;
          // Calculate redeemed points (earned - balance)
          customerPoints[email].totalRedeemed = Math.max(0, customerPoints[email].totalEarned - customerPoints[email].pointBalance);
        });
        
        return Object.values(customerPoints).map((customer, index) => ({
          id: `points_${index}`,
          customer: customer.customer,
          email: customer.email,
          mobile: customer.mobile,
          amount: customer.amount,
          date: customer.date,
          earned: customer.totalEarned,
          redeemed: customer.totalRedeemed,
          available: customer.pointBalance,
          type: 'Available Points'
        }));
        
      case 'totalPurchases':
        return filterByDateRange(customerData, it => it.transaction_date || it.created_on).map(item => ({
          id: item.id || item._id,
          customer: item.name || item.email_id?.split('@')[0] || 'Customer',
          email: item.email_id,
          date: item.transaction_date || item.created_on,
          amount: item.amount,
          points: item.point_given || 0,
          type: 'Purchase',
          mobile:item.mobile
        }));
        
      case 'monthlyIncome':
        // Return filtered customer data based on date
       
        let filteredByDate = [...customerData];
        
        if (dateFilter === 'today') {
          const today = new Date().toDateString();
          filteredByDate = filteredByDate.filter(item => {
            const itemDate = new Date(item.transaction_date || item.created_on).toDateString();
            return itemDate === today;
          });
        } else if (dateFilter === 'thisMonth') {
          const now = new Date();
          const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
          filteredByDate = filteredByDate.filter(item => {
            const itemDate = new Date(item.transaction_date || item.created_on);
            return itemDate >= startOfMonth;
          });
        } else if (dateFilter === 'custom' && customDateRange.start && customDateRange.end) {
          const startDate = new Date(customDateRange.start);
          const endDate = new Date(customDateRange.end);
          filteredByDate = filteredByDate.filter(item => {
            const itemDate = new Date(item.transaction_date || item.created_on);
            return itemDate >= startDate && itemDate <= endDate;
          });
        }
        
        return filteredByDate.map(item => ({
          id: item.id || item._id,
          customer: item.name || item.email_id?.split('@')[0] || 'Customer',
          email: item.email_id,
          date: item.transaction_date || item.created_on,
          amount: parseFloat(item.amount || 0),
          points: item.point_given || 0,
          paymentMethod: item.payment_method || ['Credit Card', 'UPI', 'Cash', 'Debit Card'][Math.floor(Math.random() * 4)],
          // status: item.status || 'Completed',
          type: 'Monthly Income',
       
        }));
        
      default:
        return [];
    }
  };

  const recentActivity = customerData
    .slice(0, 5)
    .map(item => ({
      id: item.id || item._id,
      customer: item.name || item.email_id?.split('@')[0] || 'Customer',
      email: item.email_id,
      action: ` ${item.point_given || 0} points`,
      amount: `₹${parseFloat(item.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
     
      time: new Date(item.transaction_date || item.created_on).toLocaleDateString(),
      type: 'earning'
    }));

  // Get real offers from profile data
  const currentOffers = profileData?.shopDetails?.flatMap(shop =>
    shop.offers?.map(offer => ({
      id: offer.id,
      name: offer.title,
      description: offer.subtitle || 'Special offer',
      coupon: offer.code,
      views: Math.floor(Math.random() * 1000) + 500,
      used: Math.floor(Math.random() * 50) + 10,
      conversion: `${(Math.random() * 5 + 1).toFixed(1)}%`,
      validity: offer.expiry,
      // status: 'Active',
      purchases: `₹${(Math.random() * 50000 + 20000).toLocaleString()}`,
      shopName: shop.shopName,
      category: shop.category,
      startDate: '2024-01-01',
      endDate: '2024-12-31',
      minPurchase: '₹300',
      maxDiscount: '₹100'
    })) || []
  ) || [];

  // Sales History Data from API
  const salesHistory = customerData
    .filter(item => parseFloat(item.amount || 0) > 0)
    .map(item => ({
      id: item.id || item._id,
      customer: item.name || item.email_id?.split('@')[0] || 'Customer',
      email: item.email_id,
      amount: parseFloat(item.amount || 0),
      items: `${Math.floor(Math.random() * 5) + 1} items`, // Mock items count
      date: item.transaction_date || item.created_on,
      status: item.status || 'Completed',
      paymentMethod: item.payment_method || ['Credit Card', 'UPI', 'Cash', 'Debit Card'][Math.floor(Math.random() * 4)],
      points: item.point_given || 0
    }));

  const StatCard = ({ icon: Icon, title, value, subtitle, cardType, color = 'blue' }) => {
    const handleCardClick = () => {
      // Navigate to Sales History page when clicking Total Sales
      if (cardType === 'monthlyIncome') {
        setShowSalesHistory(true);
        return;
      }
      // If Total Points card is clicked, default to Customers (Total Points) view
      if (cardType === 'availablePoints') {
        const data = generateCardData(cardType);
        setCardData(data);
        setSelectedCard(cardType);
        setAvailablePointsView('customers');
        setSearchTerm('');
        setSortField('');
        setCurrentPage(1);
        setDateFilter('all');
        setShowCardDetails(true);
        return;
      }
      const data = generateCardData(cardType);
      setCardData(data);
      setSelectedCard(cardType);
      setShowCardDetails(true);
    };

    const colorClasses = {
      blue: 'from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700',
      green: 'from-green-500 to-green-600 hover:from-green-600 hover:to-green-700',
      purple: 'from-purple-500 to-purple-600 hover:from-purple-600 hover:to-purple-700',
      orange: 'from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700'
    };

    return (
      <div 
        onClick={handleCardClick}
        className={`bg-gradient-to-r ${colorClasses[color]} rounded-xl p-6 text-white cursor-pointer transform hover:scale-105 transition-all duration-200 shadow-lg hover:shadow-xl`}
      >
        <div className="flex items-center justify-between">
          <div>
            <p className="text-white/80 text-sm font-medium">{title}</p>
            <p className="text-3xl font-bold mt-2">{value}</p>
            <p className="text-white/70 text-xs mt-1">{subtitle}</p>
          </div>
          <div className="bg-white/20 p-3 rounded-lg">
            <Icon className="w-8 h-8" />
          </div>
        </div>
      </div>
    );
  };

  // Filter function for activities
  const filterData = (data, searchTerm, filterType) => {
    let filtered = data;

    // Filter by search term
    // if (searchTerm) {
    //   filtered = filtered.filter(item => {
    //     const customer = item.customer || '';
    //     const email = item.email || '';
    //     const mobile = item.mobile || '';
    //     const amount = item.amount != null ? String(item.amount) : '';
    //     const action = item.action || '';
    //     const source = item.source || '';
    //     const description = item.description || '';

    //     const q = searchTerm.toLowerCase();
    //     return customer.toLowerCase().includes(q) ||
    //            email.toLowerCase().includes(q) ||
    //            mobile.toLowerCase().includes(q) ||
    //            amount.toLowerCase().includes(q) ||
    //            action.toLowerCase().includes(q) ||
    //            source.toLowerCase().includes(q) ||
    //            description.toLowerCase().includes(q);
    //   });
    // }

    if (searchTerm) {
      const q = searchTerm.toLowerCase();
      filtered = filtered.filter(item => {
        const customer = (item.customer || '').toString();
        const email = (item.email || '').toString();
        const mobile = (item.mobile || '').toString();
        const amount = (item.amount != null ? item.amount : '').toString();
        const action = (item.action || '').toString();
        const source = (item.source || '').toString();
        const description = (item.description || '').toString();
        const date = (item.date ? new Date(item.date).toLocaleDateString() : '').toString();

        return customer.toLowerCase().includes(q) ||
               email.toLowerCase().includes(q) ||
               mobile.toLowerCase().includes(q) ||
               amount.toLowerCase().includes(q) ||
               date.toLowerCase().includes(q) ||
               action.toLowerCase().includes(q) ||
               source.toLowerCase().includes(q) ||
               description.toLowerCase().includes(q);
      });
    }
    


    // Filter by type
    if (filterType !== 'all') {
      filtered = filtered.filter(item => item.type === filterType);
    }

    return filtered;
  };

  // Sorting function (restored)
  const sortData = (data, field, direction) => {
    if (!field) return data;
    return [...data].sort((a, b) => {
      let aValue = a[field];
      let bValue = b[field];
      // Handle currency strings like "₹1,200"
      if (typeof aValue === 'string' && aValue.includes('₹')) {
        aValue = parseFloat(aValue.replace(/[₹,]/g, ''));
        bValue = parseFloat(bValue.replace(/[₹,]/g, ''));
      }
      // Case-insensitive compare for strings
      if (typeof aValue === 'string' && typeof bValue === 'string') {
        aValue = aValue.toLowerCase();
        bValue = bValue.toLowerCase();
      }
      return direction === 'asc' ? (aValue > bValue ? 1 : -1) : (aValue < bValue ? 1 : -1);
    });
  };

  // Pagination function
  const paginateData = (data, page, itemsPerPage) => {
    const startIndex = (page - 1) * itemsPerPage;
    const endIndex = startIndex + itemsPerPage;
    return data.slice(startIndex, endIndex);
  };

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
    setCurrentPage(1); // Reset to first page when sorting
  };

  // Handle offer row click
  const handleOfferClick = (offer) => {
    setSelectedOffer(offer);
    setShowOfferReceiptModal(true);
  };

  // Handle QR scan result
  const handleQRScan = (scannedData) => {
    toast.success(`Transaction processed for ${scannedData.customerName || scannedData.customerId}`);
    // Process the scanned transaction data
    console.log('Scanned QR Data:', scannedData);
    
    // Add to recent activity (mock implementation)
    const newActivity = {
      id: Date.now(),
      customer: scannedData.customerName || scannedData.customerId,
      email: scannedData.email || 'customer@example.com',
      action: `Purchased items worth ₹${scannedData.amount}`,
      amount: `₹${scannedData.amount}`,
      time: new Date().toLocaleTimeString(),
      type: 'purchase'
    };
    
    // In a real app, you would make an API call here to process the transaction
    setShowQRScanner(false);
  };

  // Handle menu actions from SharedHeader
  const handleMenuAction = async (action, data = null) => {
    switch (action) {
      case 'manageOffers':
        setShowManageOffers(true);
        break;
      case 'salesHistory':
        setShowSalesHistory(true);
        break;
      case 'profileUpdate':
        await fetchLatestProfileForCurrentUser();
        setShowProfileModal(true);
        break;
      case 'patchSettings':
        setShowPatchSettingsModal(true);
        break;
      case 'shopDetails':
        await fetchLatestProfileForCurrentUser();
        setShowShopDetailsModal(true);
        break;
      case 'buyPoints':
        setShowBuyPointsModal(true);
        break;
      case 'qrScanner':
        setShowQRScanner(true);
        break;
      case 'qrGenerator':
        setShowQRGenerator(true);
        break;
        // case 'creditByMobile':
        //   // setCreditMobile('');
        //   // setCreditAmount('');
        //   // setShowCreditByMobileModal(true);
        //   navigate('/business/credit-by-mobile');
        //   break;
      case 'createOffer':
        setShowCreateOfferModal(true);
        break;
      case 'rewardConfig':
        setShowRewardConfigModal(true);
        break;
      case 'claimRequests':
        navigate('/business/claim-requests');
        break;
        case 'topupRequests':
  // Open Total Points card in Top-up Requests mode
  setSelectedCard('availablePoints');
  setAvailablePointsView('topups');
  setShowCardDetails(true);
  setSearchTerm('');
  setSortField('');
  setCurrentPage(1);
  setDateFilter('all');
  break;
        
      //   setShowBusinessAnalyticsModal(true);
      //   break;
      default:
        break;
    }
  };

  // Approve/Reject Claim Requests
const computePointsForAmount = (amt) => {
  const perRupee = 0.1; // fallback conversion, can be made configurable
  const n = Number(amt) || 0;
  return Math.max(0, Math.floor(n * perRupee));
};

// Credit points to a customer found by mobile number
const handleCreditByMobile = async () => {
  try {
    const mobile = String(creditMobile || '').trim();
    const amt = Number(creditAmount) || 0;
    if (!mobile || !amt) {
      toast.error('Enter mobile and amount');
      return;
    }
    setCreditLoading(true);
    // Find customer by mobile in profiles
    const prof = await AllProfile({});
    const list = Array.isArray(prof.extractedData) ? prof.extractedData : (prof.extractedData?.records || []);
    const cust = (list || []).find(p => (String(p.mobile || '').trim()) === mobile);
    if (!cust) {
      toast.error('No customer found with this mobile');
      return;
    }
    const points = computePointsForAmount(amt);
    await createCustomerTransaction({
      email_id: cust.email || cust.customerEmail,
      name: cust.firstName ? `${cust.firstName} ${cust.lastName || ''}`.trim() : (cust.email?.split('@')[0] || 'Customer'),
      amount: amt,
      point_given: points,
      transaction_date: new Date().toISOString(),
      payment_method: 'UPI',
      status: 'Completed',
    });
    toast.success(`Credited ${points} pts`);
    setShowCreditByMobileModal(false);
    setCreditMobile('');
    setCreditAmount('');
    await fetchBusinessData();
  } catch (e) {
    console.error('Credit by mobile failed', e);
    toast.error('Failed to credit points');
  } finally {
    setCreditLoading(false);
  }
};

  // Approve claim with entered amount (from modal)
  const approveClaimWithAmount = async () => {
    if (!approveTarget) return;
    try {
      const amt = Number(approveAmount) || 0;
      if (amt <= 0) {
        toast.error('Enter a valid amount');
        return;
      }
      // 1) Mark claim as approved with final amount
      await updateClaimRequest(approveTarget.id, {
        status: 'approved',
        amount: amt,
        approvedDate: new Date().toISOString(),
      });

      // 2) Credit points to customer
      const points = computePointsForAmount(amt);
      await createCustomerTransaction({
        email_id: approveTarget.customerEmail,
        name: approveTarget.customerName || (approveTarget.customerEmail?.split('@')[0] || 'Customer'),
        amount: amt,
        point_given: points,
        transaction_date: new Date().toISOString(),
        payment_method: 'UPI',
        status: 'Completed',
      });

      toast.success(`Approved claim for ₹${amt.toLocaleString()} and credited ${points} pts`);
      setShowApproveModal(false);
      setApproveTarget(null);
      setApproveAmount('');
      await fetchBusinessData();
    } catch (e) {
      console.error('Approve claim (modal) failed', e);
      toast.error('Failed to approve claim');
    }
  };

  const handleRejectClaim = async (req) => {
    try {
      await updateClaimRequest(req.id, { status: 'rejected' });
      toast.success('Claim rejected');
      // Update local list quickly
      setClaimRequests(prev => prev.map(x => x.id === req.id ? { ...x, status: 'rejected' } : x));
    } catch (e) {
      console.error('Reject claim failed', e);
      toast.error('Failed to reject claim');
    }
  };

  // Modal Component
  const Modal = ({ isOpen, onClose, title, children, size = 'md' }) => {
    if (!isOpen) return null;

    const sizeClasses = {
      sm: 'max-w-md',
      md: 'max-w-2xl',
      lg: 'max-w-4xl',
      xl: 'max-w-6xl'
    };

    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
        <div className={`bg-white rounded-xl shadow-xl ${sizeClasses[size]} w-full max-h-[90vh] overflow-hidden`}>
          <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">{title}</h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 rounded-lg hover:bg-gray-100"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
          <div className="p-6 overflow-y-auto overflow-x-hidden max-h-[calc(100vh-150px)]">
            {children}
          </div>
        </div>
      </div>
    );
  };

  const renderCardDetails = () => {
    if (!selectedCard) return null;

    // Generate card data if not available
    const currentCardData = cardData.length > 0 ? cardData : generateCardData(selectedCard);

    // When on Total Points, support switching to a Top-up Requests view
    const baseData = (selectedCard === 'availablePoints' && availablePointsView === 'topups')
      ? (approvalRequests || []).map((r, idx) => ({
          id: r.id || `topup_${idx}`,
          customer: r.name || r.email?.split('@')[0] || 'Business',
          email: r.email,
          amount: Number(r.amount || 0),
          points: Number(r.points || 0),
          date: r.requestDate,
          status: r.status,
          type: 'Top-up Request',
        }))
      : currentCardData;

    const getCardTitle = () => {
      switch (selectedCard) {
        case 'pointsGiven': return 'Points Given';
        case 'availablePoints': return availablePointsView === 'topups' ? 'Top-up Requests' : 'Total Points';
        case 'totalPurchases': return 'Total Purchase';
        case 'monthlyIncome': 
          if (dateFilter === 'today') return "Today's Transactions";
          if (dateFilter === 'thisMonth') return "This Month's Transactions";
          if (dateFilter === 'custom') return "Custom Transactions";
          return 'All Transactions';
        default: return 'Card Details';
      }
    };

    const filteredData = filterData(baseData, searchTerm, filterType);
    const sortedData = sortData(filteredData, sortField, sortDirection);
    const paginatedData = paginateData(sortedData, currentPage, itemsPerPage);

    return (
      <div className="bg-white rounded-xl shadow-md p-6 mb-8">
        <div className="flex items-center justify-between mb-6">
          <h3 className="text-xl font-bold text-gray-900">{getCardTitle()}</h3>
          <button
            onClick={() => {
              setShowCardDetails(false);
              setSelectedCard(null);
              setSearchTerm('');
              setFilterType('all');
            }}
            className="text-gray-500 hover:text-gray-700 p-2 rounded-lg hover:bg-gray-100"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Search and Filter Controls */}
        <div className="flex flex-col sm:flex-row gap-4 mb-6">
          <div className="flex-1">
            <input
              type="text"
              placeholder={selectedCard === 'availablePoints' && availablePointsView === 'topups' ?
                'Search by name, email, amount, or date...' :
                'Search by name, email, mobile, or amount...'}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
          
          {/* Date Filter for card details */}
          {(selectedCard === 'monthlyIncome' || selectedCard === 'availablePoints' || selectedCard === 'pointsGiven' || selectedCard === 'totalPurchases') && (
            <div className="flex gap-2">
              <select
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="all">All Time</option>
                <option value="today">Today</option>
                <option value="lastWeek">Last 7 Days</option>
                <option value="lastMonth">Last 30 Days</option>
                <option value="custom">Custom Range</option>
              </select>
              
              {dateFilter === 'custom' && (
                <>
                  <input
                    type="date"
                    value={customDateRange.start}
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, start: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                  <input
                    type="date"
                    value={customDateRange.end}
                    onChange={(e) => setCustomDateRange(prev => ({ ...prev, end: e.target.value }))}
                    className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  />
                </>
              )}

              {/* Add button ONLY for Total Sales */}
    {selectedCard === 'monthlyIncome' && (
      <button
        onClick={() => {
          setAddTxnQuery('');
          setAddTxnSelected(null);
          setAddTxnAmount('');
          setShowAddTxnModal(true);
        }}
        className="px-3 py-2 rounded-lg text-white bg-blue-600 hover:bg-blue-700"
      >
        Add
      </button>
    )}

            </div>
          )}
        </div>
        
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b border-gray-200">
                {selectedCard === 'pointsGiven' && (
                  <>
                    <th 
                      className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('customer')}
                    >
                      Customer {sortField === 'customer' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('mobile')}
                    >
                      Mobile {sortField === 'mobile' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-right py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('points')}
                    >
                      Points Given {sortField === 'points' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-right py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('purchase')}
                    >
                      Purchase Amount{sortField === 'purchase' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('date')}
                    >
                      Date {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                  </>
                )}
                {selectedCard === 'availablePoints' && availablePointsView === 'customers' && (
                  <>
                    <th 
                      className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('customer')}
                    >
                      Customer {sortField === 'customer' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('mobile')}
                    >
                      Mobile {sortField === 'mobile' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-right py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('purchase')}
                    >
                      Purchase Amount{sortField === 'purchase' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    {/* <th 
                      className="text-right py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('earned')}
                    >
                      Earned {sortField === 'earned' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th> */}
                    {/* <th className="text-right py-3 px-4 font-semibold text-gray-700">Redeemed</th> */}
                    <th 
                      className="text-right py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('available')}
                    >
                      Redemption Points{sortField === 'available' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('date')}
                    >
                      Date {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-left py-3 px-4 font-semibold text-gray-700"
                    >
                      Status
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">
                      Actions
                    </th>
                  </>
                )}
                {selectedCard === 'availablePoints' && availablePointsView === 'topups' && (
                  <>
                    <th 
                      className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('email')}
                    >
                      Email {sortField === 'email' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-right py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('amount')}
                    >
                      Amount {sortField === 'amount' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-right py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('points')}
                    >
                      Points {sortField === 'points' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('date')}
                    >
                      Date {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th>
                  </>
                )}
                {selectedCard === 'totalPurchases' && (
                  <>
                    <th 
                      className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('customer')}
                    >
                      Customer {sortField === 'customer' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                                         <th className="text-left py-3 px-4 font-semibold text-gray-700">Mobile</th> 
                    <th 
                      className="text-right py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('amount')}
                    >
                     Purchase Amount {sortField === 'amount' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    {/* <th className="text-left py-3 px-4 font-semibold text-gray-700">Items</th> */}
                    <th 
                      className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('date')}
                    >
                      Date {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    {/* <th 
                      className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('status')}
                    >
                      Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th> */}
                  </>
                )}
                {selectedCard === 'monthlyIncome' && (
                  <>
                    <th 
                      className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('customer')}
                    >
                      Customer {sortField === 'customer' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                  
                    <th 
                      className="text-right py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('amount')}
                    >
                      purchase Amount {sortField === 'amount' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-right py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('points')}
                    >
                      Points Given {sortField === 'points' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-left py-3 px-4 font-semibold text-gray-700 cursor-pointer hover:bg-gray-50"
                      onClick={() => handleSort('date')}
                    >
                      Date {sortField === 'date' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="text-left py-3 px-4 font-semibold text-gray-700">Payment Method</th>
                    {/* <th className="text-left py-3 px-4 font-semibold text-gray-700">Status</th> */}
                  </>
                )}
              </tr>
            </thead>
            <tbody>
              {paginatedData.map((item, index) => (
                <tr key={item.id || index} className="border-b border-gray-100 hover:bg-gray-50">
                  {selectedCard === 'pointsGiven' && (
                    <>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-900">{item.customer}</p>
                          <p className="text-sm text-gray-500">{item.email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-blue-600 font-semibold">{item.mobile}</td>
                      <td className=" text-right py-3 px-4 text-blue-600 font-semibold">{item.points}</td>
                      {/* <td className=" text-right py-3 px-4 text-gray-700">₹{parseFloat(item.amount || 0).toLocaleString()}</td> */}
                      <td className="text-right py-3 px-4 text-gray-600 font-semibold">
          ₹{parseFloat(item.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
</td>
                      <td className="py-3 px-4 text-gray-500">{new Date(item.date).toLocaleDateString()}</td>
                    </>
                  )}
                  {selectedCard === 'availablePoints' && availablePointsView === 'customers' && (
                    <>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-900">{item.customer}</p>
                          <p className="text-sm text-gray-500">{item.email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-blue-600 font-semibold">{item.mobile}</td>
                     <td className="text-right py-3 px-4 text-blue-600 font-semibold">
  ₹{parseFloat(item.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
</td>
                      {/* <td className="py-3 px-4 text-green-600 font-semibold">{item.earned} pts</td> */}
                      {/* <td className="py-3 px-4 text-red-600 font-semibold">{item.redeemed} pts</td> */}
                      <td className=" text-right py-3 px-4 text-blue-600 font-semibold">{item.available} </td>
                      <td className="py-3 px-4 text-gray-500">{new Date(item.date).toLocaleDateString()}</td>
                      <td className="py-3 px-4 text-gray-500">—</td>
                      <td className="py-3 px-4 text-gray-500">—</td>
                    </>
                  )}
                  {selectedCard === 'availablePoints' && availablePointsView === 'topups' && (
                    <>
                      <td className="py-3 px-4 text-gray-700">{item.email}</td>
                      <td className="text-right py-3 px-4 text-blue-600 font-semibold">₹{Number(item.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                      <td className="text-right py-3 px-4 text-blue-600 font-semibold">{Number(item.points || 0).toLocaleString()}</td>
                      <td className="py-3 px-4 text-gray-500">{new Date(item.date).toLocaleDateString()}</td>
                      <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${item.status === 'pending' ? 'bg-yellow-100 text-yellow-800' : item.status === 'approved' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                          {String(item.status || '').charAt(0).toUpperCase() + String(item.status || '').slice(1)}
                        </span>
                      </td>
                    </>
                  )}
                  {selectedCard === 'totalPurchases' && (
                    <>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-900">{item.customer}</p>
                          <p className="text-sm text-gray-500">{item.email}</p>
                        </div>
                      </td>
                      <td className="py-3 px-4 text-blue-600 font-semibold">{item.mobile}</td>
                       <td className="text-right py-3 px-4 text-purple-600 font-semibold">
  ₹{parseFloat(item.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
</td>
                      {/* <td className=" text-right py-3 px-4 text-gray-700">{item.points}</td> */}
                      <td className="py-3 px-4 text-gray-500">{new Date(item.date).toLocaleDateString()}</td>
                      {/* <td className="py-3 px-4">
                        <span className="px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                          Completed
                        </span>
                      </td> */}
                    </>
                  )}
                  {selectedCard === 'monthlyIncome' && (
                    <>
                      <td className="py-3 px-4">
                        <div>
                          <p className="font-medium text-gray-900">{item.customer}</p>
                          <p className="text-sm text-gray-500">{item.email}</p>
                        </div>
                      </td>
                      {/* <td className="py-3 px-4 text-gray-700">{item.shopName}</td> */}
                       <td className="text-right py-3 px-4 text-green-600 font-semibold">
  ₹{parseFloat(item.amount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
</td>
                    <td className=" text-right py-3 px-4 text-right text-blue-600 font-semibold">{item.points} pts</td>
                      <td className="py-3 px-4 text-gray-500">{new Date(item.date).toLocaleDateString()}</td>
                      <td className="py-3 px-4 text-gray-700">{item.paymentMethod}</td>
                      {/* <td className="py-3 px-4">
                        <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                          item.status === 'Completed' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'
                        }`}>
                          {item.status}
                        </span>
                      </td> */}
                    </>
                  )}
                </tr>
              ))}

              {false && <tr></tr>}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex items-center justify-between border-t pt-6">
          <p className="text-gray-600">
            Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} items
          </p>
          <div className="flex items-center space-x-2">
            <button 
              onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Previous
            </button>
            {[...Array(Math.ceil(filteredData.length / itemsPerPage))].map((_, i) => (
              <button
                key={i + 1}
                onClick={() => setCurrentPage(i + 1)}
                className={`px-4 py-2 rounded-lg ${
                  currentPage === i + 1 
                    ? 'bg-blue-600 text-white' 
                    : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                }`}
              >
                {i + 1}
              </button>
            ))}
            <button 
              onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(filteredData.length / itemsPerPage)))}
              disabled={currentPage === Math.ceil(filteredData.length / itemsPerPage)}
              className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Show manage offers page
  if (showManageOffers) {
    return (
      <ManageOffers 
        onBack={() => setShowManageOffers(false)}
        onMenuAction={handleMenuAction}
      />
    );
  }

  // Show sales history page
  if (showSalesHistory) {
    return (
      <>
        <SalesHistoryPage 
          onBack={() => setShowSalesHistory(false)}
          onMenuAction={handleMenuAction}
          onAddTransaction={() => setShowAddTxnModal(true)}
          refreshKey={salesRefreshTick}
        />

        {/* Add Transaction Modal (kept mounted for Sales History page) */}
        <Modal
          isOpen={showAddTxnModal}
          onClose={() => {
            setShowAddTxnModal(false);
            setAddTxnQuery('');
            setAddTxnSelected(null);
            setAddTxnAmount('');
          }}
          title="Add Transaction"
          size="lg"
        >
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Customer</label>
              <input
                type="text"
                value={addTxnQuery}
                onChange={(e) => {
                  setAddTxnQuery(e.target.value);
                  setAddTxnSelected(null);
                }}
                placeholder="Search by name or mobile"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {!addTxnSelected && (
                <div className="mt-2 border rounded-lg max-h-40 overflow-auto">
                  {filteredCustomerOptions.length ? filteredCustomerOptions.map(opt => (
                    <button
                      key={opt.email}
                      type="button"
                      onClick={() => {
                        selectAddTxnCustomer(opt);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50"
                    >
                      <div className="font-medium text-gray-900">{opt.name}</div>
                      <div className="text-sm text-gray-500">{opt.email} • {opt.mobile || '—'}</div>
                      <div className="font-medium text-gray-900">{opt.amount}</div>
                    </button>
                  )) : (
                    <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
                  )}
                </div>
              )}
              {addTxnSelected && (
                <div className="mt-2 text-sm text-gray-600">
                  Selected: <span className="font-medium text-gray-900">{addTxnSelected.name}</span> • {addTxnSelected.email} • {addTxnSelected.mobile || '—'} {amount}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Amount (₹)</label>
              <input
                type="number"
                min="1"
                value={addTxnAmount}
                onChange={(e) => setAddTxnAmount(e.target.value)}
                placeholder="Enter purchase amount"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              <div className="mt-2 text-sm">
                Points to credit: <span className="font-semibold text-blue-600">{computePointsForAmount(addTxnAmount || 0)} pts</span>
              </div>
              {/* <div className="mt-1 text-sm text-gray-700">
                {(() => {
                  const amtNum = Number(addTxnAmount || 0);
                  const redeemNum = useCustomerPoints ? Number(redeemPoints || 0) : 0;
                  const payable = Math.max(0, amtNum - redeemNum);
                  return (
                    <>Total payable: <span className="font-semibold">₹{payable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span></>
                  );
                })()}
              </div> */}
              <div className="mt-3 space-y-3">
                <label className="flex items-center gap-2 text-sm text-gray-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4"
                    checked={useCustomerPoints}
                    onChange={(e) => {
                      setUseCustomerPoints(e.target.checked);
                      if (!e.target.checked) {
                        setRedeemPoints('');
                        setOtpSent(false); setOtpCode(''); setOtpInput(''); setOtpVerified(false);
                      }
                    }}
                  />
                  Use customer points (manual redemption)
                </label>
                {useCustomerPoints && (
                  <>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 items-end">
                      <div>
                        <label className="block text-sm text-gray-700 mb-1">Redeem Points</label>
                        <input
                          type="number"
                          min="1"
                          max={Math.floor(Number(addTxnAmount || 0) / RATE_PER_POINT) || 0}
                          value={redeemPoints}
                          onChange={(e) => setRedeemPoints(e.target.value)}
                          placeholder="Enter points to redeem"
                          className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                        />
                      </div>
                      <div className="text-sm text-gray-700">
                        {/* <div className="mb-1">Worth: <span className="font-semibold">₹{Number(redeemPoints || 0) * RATE_PER_POINT}</span></div> */}
                        <div>Available: <span className="font-semibold">{(() => {
                          const email = addTxnSelected?.email;
                          const items = (customerData || []).filter(x => (x.email_id || x.email) === email);
                          if (!items.length) return 0;
                          // Total points = sum of point_given (positives and negatives)
                          return items.reduce((s, it) => s + Number(it.point_given || 0), 0);
                        })()}</span> pts</div>
                        {/* <div className="text-gray-500">Max for this amount: <span className="font-semibold">{Math.floor(Number(addTxnAmount || 0) / RATE_PER_POINT) || 0}</span> pts</div> */}
                      </div>
                    </div>
                    <div className="mt-2 flex flex-col sm:flex-row gap-2">
                      {!otpSent ? (
                        <button type="button" onClick={async () => {
                          try {
                            if (!addTxnSelected?.email) { toast.error('Select a customer'); return; }
                            const amtOk = Number(addTxnAmount) > 0;
                            const redeemOk = Number(redeemPoints) > 0;
                            if (!amtOk) { toast.error('Please enter purchase amount'); return; }
                            if (!redeemOk) { toast.error('Please enter redeem points'); return; }
                            // const maxRedeem = Math.floor(Number(addTxnAmount) / RATE_PER_POINT);
                            // if (Number(redeemPoints) > maxRedeem) { toast.error(`Redeem points cannot exceed ${maxRedeem} for this amount`); return; }
                            const code = String(Math.floor(1000 + Math.random()*9000)); // 4-digit OTP
                            setOtpCode(code);
                            setOtpSent(true);
                            setOtpVerified(false);
                            // Create a lightweight notification transaction so customer sees OTP in recent activity
                            await createCustomerTransaction({
                              email_id: addTxnSelected.email,
                              name: addTxnSelected.name || addTxnSelected.email.split('@')[0],
                              amount: 0,
                              point_given: 0,
                              transaction_date: new Date().toISOString(),
                              payment_method: 'Redeem',
                              status: 'otp_sent',
                              otp_code: code,
                            });
                            toast.success(`OTP sent to ${addTxnSelected.email}`);
                          } catch (e) {
                            console.error('Send OTP failed', e);
                            toast.error('Failed to send OTP');
                          }
                        }}
                        disabled={!addTxnSelected?.email || !(Number(addTxnAmount) > 0) || !(Number(redeemPoints) > 0)}
                        className={`px-4 py-2 rounded-lg w-full sm:w-auto ${(!addTxnSelected?.email || !(Number(addTxnAmount) > 0) || !(Number(redeemPoints) > 0)) ? 'bg-indigo-300 text-white cursor-not-allowed' : 'bg-indigo-600 text-white hover:bg-indigo-700'}`}
                        >Send OTP</button>
                      ) : (
                        <div className="flex items-center gap-2 w-full">
                          <input
                            type="text"
                            value={otpInput}
                            onChange={(e)=> setOtpInput(e.target.value)}
                            placeholder="Enter OTP"
                            className="flex-1 px-3 py-2 border rounded-lg"
                          />
                          <button type="button" onClick={() => {
                            if (otpInput && otpInput === otpCode) { setOtpVerified(true); toast.success('OTP verified'); }
                            else { setOtpVerified(false); toast.error('Invalid OTP'); }
                          }} className="px-3 py-2 bg-green-600 text-white rounded-lg">Verify</button>
                        </div>
                      )}
                    </div>
                    {otpVerified && <div className="text-sm text-green-700">OTP verified ✔</div>}

                    {/* Net change and projected balance preview */}
                    <div className="mt-2 text-sm bg-gray-50 border rounded-lg p-3">
                      {(() => {
                        const email = addTxnSelected?.email;
                        const items = (customerData || []).filter(x => (x.email_id || x.email) === email);
                        const available = items.reduce((s, it) => s + Number(it.point_given || 0), 0);
                        const credit = computePointsForAmount(addTxnAmount || 0);
                        const redeem = Number(redeemPoints || 0);
                        const net = credit - redeem;
                        const projected = available + net;
                        const amtNum = Number(addTxnAmount || 0);
                        const payable = Math.max(0, amtNum - redeem);
                        return (
                          <div className="flex flex-col sm:flex-row gap-3 sm:items-center sm:justify-between">
                            <div>
                              <span className="text-gray-600">Net change:</span>{' '}
                              <span className={`font-semibold ${net >= 0 ? 'text-green-600' : 'text-red-600'}`}>{net >= 0 ? '+' : ''}{net} pts</span>
                            </div>
                            <div>
                              <span className="text-gray-600">Projected balance after save:</span>{' '}
                              <span className="font-semibold text-gray-900">{projected}</span> pts
                            </div>
                            <div className="text-gray-700">
                              Total payable: <span className="font-semibold">₹{payable.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                            </div>
                          </div>
                        );
                      })()}
                    </div>
                  </>
                )}
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                onClick={handleAddTxnReject}
                disabled={addTxnLoading}
              >
                Close
              </button>
              <button
                type="button"
                className={`px-4 py-2 rounded-lg text-white ${ (addTxnLoading || (useCustomerPoints && !otpVerified)) ? 'bg-gray-300 cursor-not-allowed' : 'bg-green-600 hover:bg-green-700' }`}
                onClick={handleAddTxnApprove}
                disabled={addTxnLoading || (useCustomerPoints && !otpVerified)}
                title={useCustomerPoints && !otpVerified ? 'Verify OTP to enable Save' : undefined}
              >
                {addTxnLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </Modal>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <SharedHeader 
        title={businessConfig.businessName}
        subtitle="Business Dashboard"
        onMenuAction={handleMenuAction}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <StatCard
            icon={Target}
            title="Total Points"
            value={(Math.max(0, (businessStats.availablePoints + approvedPointsSum - approvedClaimPointsSum))).toLocaleString()}
            subtitle={`Approved top-ups: ${approvedPointsSum.toLocaleString()} pts (₹${approvedAmountSum.toLocaleString()})  pts`}
            cardType="availablePoints"
            color="green"
          />
          <StatCard
            icon={Award}
            title="Points Given"
            value={businessStats.pointsGiven.toLocaleString()}
            subtitle="Total points distributed"
            cardType="pointsGiven"
            color="blue"
          />
         
          <StatCard
            icon={ShoppingCart}
            title="Total Purchases"
            value={businessStats.totalPurchases}
            subtitle="Completed transactions"
            cardType="totalPurchases"
            color="purple"
          />
          <StatCard
            icon={DollarSign}
            title="Total Sales"
            value={`₹${(businessStats.monthlyIncome / 1000).toFixed(0)}K`}
            subtitle="Total revenue"
            cardType="monthlyIncome"
            color="orange"
          />
        </div>

        {/* Card Details */}
        {showCardDetails && renderCardDetails()}


        {/* Recent Business Activity */}
        {!showCardDetails && activeTab === 'dashboard' && (
          <div className="bg-white rounded-xl shadow-md p-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-gray-900">Recent Business Activity</h3>
              <div className="flex items-center space-x-2">
                <input
                  type="text"
                  placeholder="Search activities..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
                <button
                  onClick={() => setShowViewAllActivityModal(true)}
                  className={`${theme.button} px-4 py-2 rounded-lg text-sm`}
                >
                  View All
                </button>
              </div>
            </div>
            
            <div className="space-y-4">
              {filterData(recentActivity, searchTerm, filterType).map((activity) => (
                <div key={activity.id} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
                  <div className="flex items-center space-x-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center ${
                      activity.type === 'earning' ? 'bg-green-100' : 'bg-blue-100'
                    }`}>
                      {activity.type === 'earning' ? (
                        <TrendingUp className={`w-5 h-5 ${activity.type === 'earning' ? 'text-green-600' : 'text-blue-600'}`} />
                      ) : (
                        <Gift className={`w-5 h-5 ${activity.type === 'earning' ? 'text-green-600' : 'text-blue-600'}`} />
                      )}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">{activity.customer}</p>
                      <p className="text-sm text-gray-600">{activity.action}</p>
                      <p className="text-xs text-gray-500">{activity.email}</p>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-right font-semibold text-gray-900">{activity.amount}</p>
                    <p className="text-sm text-gray-500">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* No results message for activities */}
            {filterData(recentActivity, searchTerm, filterType).length === 0 && (
              <div className="text-center py-8 text-gray-500">
                <p>No activities found matching your search criteria.</p>
              </div>
            )}
          </div>
        )}

       
        {/* Approve Claim Modal */}
        <Modal
          isOpen={showApproveModal}
          onClose={() => {
            setShowApproveModal(false);
            setApproveTarget(null);
            setApproveAmount('');
          }}
          title="Approve Claim"
          size="sm"
        >
          {approveTarget ? (
            <div className="space-y-5">
              <div className="text-sm text-gray-700">
                <div className="font-semibold text-gray-900">Customer</div>
                <div>{approveTarget.customerName || 'Customer'}</div>
                <div className="text-gray-500">{approveTarget.customerEmail}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Approved Amount (₹)</label>
                <input
                  type="number"
                  value={approveAmount}
                  onChange={(e) => setApproveAmount(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  min="0"
                  step="0.01"
                />
              </div>

              <div className="text-sm text-gray-700">
                <div>Points to credit: <span className="font-semibold text-blue-600">{computePointsForAmount(approveAmount)} pts</span></div>
              </div>

              <div className="flex justify-end gap-3 pt-2">
                <button
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                  onClick={() => {
                    setShowApproveModal(false);
                    setApproveTarget(null);
                    setApproveAmount('');
                  }}
                >
                  Cancel
                </button>
                <button
                  className="px-4 py-2 rounded-lg text-white bg-green-600 hover:bg-green-700"
                  onClick={approveClaimWithAmount}
                >
                  Confirm & Send
                </button>
              </div>
            </div>
          ) : (
            <div className="text-center text-gray-500">No claim selected</div>
          )}
        </Modal>

        {/* Add Transaction Modal */}
        <Modal
          isOpen={showAddTxnModal}
          onClose={() => {
            setShowAddTxnModal(false);
            setAddTxnQuery('');
            setAddTxnSelected(null);
            setAddTxnAmount('');
          }}
          title="Add Transaction"
          size="sm"
        >
          <div className="space-y-5">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Customer</label>
              <input
                type="text"
                value={addTxnQuery}
                onChange={(e) => {
                  setAddTxnQuery(e.target.value);
                  setAddTxnSelected(null);
                }}
                placeholder="Search by name or mobile"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              />
              {!addTxnSelected && (
                <div className="mt-2 border rounded-lg max-h-40 overflow-auto">
                  {filteredCustomerOptions.length ? filteredCustomerOptions.map(opt => (
                    <button
                      key={opt.email}
                      type="button"
                      onClick={() => {
                        selectAddTxnCustomer(opt);
                      }}
                      className="w-full text-left px-3 py-2 hover:bg-gray-50"
                    >
                      <div className="font-medium text-gray-900">{opt.name}</div>
                      <div className="text-sm text-gray-500">{opt.email} • {opt.mobile || '—'}</div>
                      <div className="font-medium text-gray-900">{opt.amount}</div>
                    </button>
                  )) : (
                    <div className="px-3 py-2 text-sm text-gray-500">No matches</div>
                  )}
                </div>
              )}
              {addTxnSelected && (
                <div className="mt-2 text-sm text-gray-600">
                  Selected: <span className="font-medium text-gray-900">{addTxnSelected.name}</span> • {addTxnSelected.email} • {addTxnSelected.mobile || '—'} {amount}
                </div>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Purchase Amount (₹)</label>
              <input
                type="number"
                min="1"
                value={addTxnAmount}
                readOnly
                placeholder="Select a customer to auto-fill amount"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-gray-50 focus:ring-0 cursor-not-allowed"
              />
              <div className="mt-2 text-sm">
                Points to credit: <span className="font-semibold text-blue-600">{computePointsForAmount(addTxnAmount || 0)} pts</span>
              </div>
              {/* <div className="mt-1 text-sm text-gray-700">
                Total payable: <span className="font-semibold">₹{Number(addTxnAmount || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
              </div> */}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                onClick={handleAddTxnReject}
                disabled={addTxnLoading}
              >
                Close
              </button>
              <button
                type="button"
                className="px-4 py-2 rounded-lg text-white bg-green-600 hover:bg-green-700 disabled:opacity-50"
                onClick={handleAddTxnApprove}
                disabled={addTxnLoading}
              >
                {addTxnLoading ? 'Saving...' : 'Save'}
              </button>
            </div>
          </div>
        </Modal>

        {/* Modal Components */}
        <Modal isOpen={showProfileModal} onClose={() => setShowProfileModal(false)} title="Profile Update" size="lg">
          <form
            className="space-y-6"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                const fd = new FormData(e.currentTarget);
                const docId = profileData?._id || profileData?.id || profileData?.profile_id ;
                if (!docId) {
                  toast.error('Profile not loaded');
                  return;
                }
                const payload = {
                  // Keep keys aligned with backend document
                  email: fd.get('email') || profileData?.email,
                  phone: fd.get('phone') || profileData?.phone,
                  registrationType: fd.get('registrationType') || profileData?.registrationType,
                  address: fd.get('address') || profileData?.address,
                  profile_id:currentUser.uid
                };
                // Also optionally map a business name into first/last or shopDetails[0].shopName
                const businessName = fd.get('businessName');
                if (businessName) {
                  // If there is at least one shop, set its shopName; else set firstName
                  const shops = Array.isArray(profileData?.shopDetails) ? JSON.parse(JSON.stringify(profileData.shopDetails)) : [];
                  if (shops[0]) {
                    shops[0].shopName = businessName;
                    payload.shopDetails = shops;
                  } else {
                    payload.firstName = businessName;
                  }
                }
                const category = fd.get('category');
                if (category) {
                  const shops = Array.isArray(payload.shopDetails || profileData?.shopDetails)
                    ? JSON.parse(JSON.stringify(payload.shopDetails || profileData.shopDetails))
                    : [];
                  if (shops[0]) {
                    shops[0].category = category;
                    payload.shopDetails = shops;
                  }
                }

                await PutBusinessProfile(docId, payload);
                toast.success('Profile updated successfully!');
                setShowProfileModal(false);
                await fetchLatestProfileForCurrentUser();
              } catch (err) {
                console.error('Profile update failed:', err);
                toast.error('Failed to update profile');
              }
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Business Name</label>
                <input
                  type="text"
                  name="businessName"
                  defaultValue={
                    profileData?.shopDetails?.[0]?.shopName ||
                    [profileData?.firstName, profileData?.lastName].filter(Boolean).join(' ') ||
                    currentUser?.displayName || 'My Business'
                  }
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Email Address</label>
                <input
                  type="email"
                  name="email"
                  defaultValue={profileData?.email || currentUser?.email || 'business@example.com'}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Phone Number</label>
                <input
                  type="tel"
                  name="phone"
                  defaultValue={profileData?.phone || ''}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Business Category</label>
                <select name="category" defaultValue={profileData?.shopDetails?.[0]?.category || ''} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                  <option value="">Select category</option>
                  <option value="restaurant">Restaurant</option>
                  <option value="retail">Retail</option>
                  <option value="services">Services</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="beauty">Beauty</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Registration Type</label>
                <select name="registrationType" defaultValue={(profileData?.registrationType || '').toLowerCase()} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                  <option value="prop">Proprietorship (Prop)</option>
                  <option value="non-registration">Non-Registration</option>
                  <option value="private">Private</option>
                </select>
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
              <textarea
                rows="3"
                name="address"
                defaultValue={profileData?.address || ''}
                className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
              ></textarea>
            </div>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setShowProfileModal(false)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`${theme.button} px-6 py-3 rounded-lg`}
              >
                Update Profile
              </button>
            </div>
          </form>
        </Modal>

        <Modal isOpen={showEditBusinessModal} onClose={() => setShowEditBusinessModal(false)} title="Edit Business" size="lg">
          <form className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Fixed Discount (%)</label>
                <input
                  type="number"
                  defaultValue="10"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Points per Rupee</label>
                <input
                  type="number"
                  step="0.01"
                  defaultValue="0.1"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Redemption Points</label>
                <input
                  type="number"
                  defaultValue="100"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Points per Transaction</label>
                <input
                  type="number"
                  defaultValue="1000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setShowEditBusinessModal(false)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`${theme.button} px-6 py-3 rounded-lg`}
                onClick={(e) => {
                  e.preventDefault();
                  toast.success('Business settings updated successfully!');
                  setShowEditBusinessModal(false);
                }}
              >
                Save Changes
              </button>
            </div>
          </form>
        </Modal>

        <Modal isOpen={showPatchSettingsModal} onClose={() => setShowPatchSettingsModal(false)} title="Patch Settings" size="md">
          <div className="text-center py-8">
            <Settings className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Settings Configuration</h3>
            <p className="text-gray-600 mb-6">Advanced settings and configuration options are coming soon.</p>
            <button
              onClick={() => setShowPatchSettingsModal(false)}
              className={`${theme.button} px-6 py-2 rounded-lg`}
            >
              Close
            </button>
          </div>
        </Modal>

        <Modal isOpen={showShopDetailsModal} onClose={() => setShowShopDetailsModal(false)} title="Shop Details" size="xl">
          <form
            className="space-y-6"
            onSubmit={async (e) => {
              e.preventDefault();
              try {
                const form = e.currentTarget;
                const fd = new FormData(form);
                const shop = {
                  shopName: fd.get('shopName') || '',
                  mobile: fd.get('mobile') || '',
                  specialDiscount: fd.get('specialDiscount') ? Number(fd.get('specialDiscount')) : undefined,
                  category: fd.get('category') || '',
                  location: {
                    lat: fd.get('lat') ? parseFloat(fd.get('lat')) : undefined,
                    lng: fd.get('lng') ? parseFloat(fd.get('lng')) : undefined,
                  },
                  rating: fd.get('rating') ? parseFloat(fd.get('rating')) : undefined,
                  description: fd.get('description') || '',
                };
                // Resolve real _id by querying backend with current profile_id
                const uid = currentUser?.uid;
                if (!uid) {
                  toast.error('User not logged in');
                  return;
                }
                let docId = profileData?._id || profileData?.id || null;
                try {
                  const resp = await getProfileUpdate({ profile_id: uid });
                  const list = Array.isArray(resp?.extractedData)
                    ? resp.extractedData
                    : (resp?.extractedData?.records || resp?.extractedData || []);
                  const match = Array.isArray(list)
                    ? list.find(p => p?.profile_id === uid)
                    : (list && list.profile_id === uid ? list : null);
                  if (match?._id || match?.id) {
                    docId = match._id || match.id;
                  }
                } catch (e) {
                  console.warn('Profile lookup failed', e);
                }
                if (!docId) {
                  toast.error('Profile not found. Please create your profile first.');
                  return;
                }

                const existing = Array.isArray(profileData?.shopDetails) ? JSON.parse(JSON.stringify(profileData.shopDetails)) : [];
                const targetName = (shop.shopName || '').trim().toLowerCase();
                const idx = existing.findIndex(s => (s?.shopName || '').trim().toLowerCase() === targetName);
                let updatedShops;
                if (idx >= 0) {
                  const merged = { ...existing[idx] };
                  const apply = (k, v) => {
                    if (v === undefined || v === null) return;
                    if (typeof v === 'string' && v.trim() === '') return;
                    merged[k] = v;
                  };
                  apply('mobile', shop.mobile);
                  apply('specialDiscount', shop.specialDiscount);
                  apply('category', shop.category);
                  if (shop.location && typeof shop.location.lat === 'number' && typeof shop.location.lng === 'number') {
                    merged.location = shop.location;
                  }
                  apply('rating', shop.rating);
                  apply('description', shop.description);
                  updatedShops = [...existing];
                  updatedShops[idx] = merged;
                } else {
                  updatedShops = [...existing, shop];
                }
                await PutBusinessProfile(docId, { shopDetails: updatedShops });
                toast.success('Shop details saved!');
                setShowShopDetailsModal(false);
                // Optionally refresh profile
                fetchBusinessData();
              } catch (err) {
                console.error('Saving shop details failed:', err);
                toast.error('Failed to save shop details');
              }
            }}
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Shop Name</label>
                <input
                  type="text"
                  name="shopName"
                  defaultValue={profileData?.shopDetails?.[0]?.shopName || ''}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Mobile</label>
                <input
                  type="tel"
                  name="mobile"
                  defaultValue={profileData?.shopDetails?.[0]?.mobile || ''}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Special Discount (%)</label>
                <input
                  type="number"
                  name="specialDiscount"
                  defaultValue={profileData?.shopDetails?.[0]?.specialDiscount ?? ''}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Category</label>
                <select name="category" defaultValue={profileData?.shopDetails?.[0]?.category || ''} className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500">
                  <option value="">Select category</option>
                  <option value="restaurant">Restaurant</option>
                  <option value="retail">Retail</option>
                  <option value="services">Services</option>
                  <option value="healthcare">Healthcare</option>
                  <option value="beauty">Beauty</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location (Latitude)</label>
                <input
                  type="number"
                  step="any"
                  name="lat"
                  defaultValue={profileData?.shopDetails?.[0]?.location?.lat ?? ''}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Location (Longitude)</label>
                <input
                  type="number"
                  step="any"
                  name="lng"
                  defaultValue={profileData?.shopDetails?.[0]?.location?.lng ?? ''}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Rating</label>
                <input
                  type="number"
                  min="1"
                  max="5"
                  step="0.1"
                  name="rating"
                  defaultValue={profileData?.shopDetails?.[0]?.rating ?? ''}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
            </div>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setShowShopDetailsModal(false)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`${theme.button} px-6 py-3 rounded-lg`}
              >
                Add Shop
              </button>
            </div>
          </form>
        </Modal>

        <Modal isOpen={showCreateOfferModal} onClose={() => setShowCreateOfferModal(false)} title="Create Multiple Offers" size="xl">
          <div className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className="text-lg font-semibold text-gray-900">Manage Offers</h3>
              <button
                onClick={() => {
                  // Add new offer logic
                  toast.success('New offer added!');
                }}
                className={`${theme.button} px-4 py-2 rounded-lg flex items-center space-x-2`}
              >
                <span>+ Add Offer</span>
              </button>
            </div>
            
            {/* Offer Form */}
            <form className="space-y-6 border-b pb-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Offer Name</label>
                  <input
                    type="text"
                    placeholder="Summer Special Discount"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Coupon Code</label>
                  <input
                    type="text"
                    placeholder="SUMMER20"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Validity (Days)</label>
                  <input
                    type="number"
                    placeholder="30"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Offer Photo</label>
                  <input
                    type="file"
                    accept="image/*"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Description</label>
                <textarea
                  rows="3"
                  placeholder="Describe your offer in detail..."
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                ></textarea>
              </div>
            </form>

            {/* Existing Offers List */}
            <div className="space-y-4">
              <h4 className="text-md font-semibold text-gray-900">Current Offers</h4>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {[
                  { name: 'Summer Special', coupon: 'SUMMER20', validity: '15 days left', status: 'Active' },
                  { name: 'New Customer', coupon: 'WELCOME10', validity: '30 days left', status: 'Active' },
                  { name: 'Weekend Deal', coupon: 'WEEKEND15', validity: 'Expired', status: 'Inactive' }
                ].map((offer, index) => (
                  <div key={index} className="border rounded-lg p-4 hover:bg-gray-50">
                    <div className="flex justify-between items-start mb-2">
                      <h5 className="font-medium text-gray-900">{offer.name}</h5>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        offer.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                      }`}>
                        {offer.status}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600">Code: {offer.coupon}</p>
                    <p className="text-sm text-gray-600">{offer.validity}</p>
                  </div>
                ))}
              </div>
            </div>

            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setShowCreateOfferModal(false)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Close
              </button>
              <button
                type="submit"
                className={`${theme.button} px-6 py-3 rounded-lg`}
                onClick={(e) => {
                  e.preventDefault();
                  toast.success('Offers updated successfully!');
                  setShowCreateOfferModal(false);
                }}
              >
                Save All Offers
              </button>
            </div>
          </div>
        </Modal>

        <Modal isOpen={showRewardConfigModal} onClose={() => setShowRewardConfigModal(false)} title="Reward Configuration" size="lg">
          <form className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Points per Rupee</label>
                <input
                  type="number"
                  step="0.01"
                  defaultValue="0.1"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-sm text-gray-500 mt-1">How many points customers earn per rupee spent</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Minimum Redemption Points</label>
                <input
                  type="number"
                  defaultValue="100"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-sm text-gray-500 mt-1">Minimum points required to redeem rewards</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Maximum Points per Transaction</label>
                <input
                  type="number"
                  defaultValue="1000"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-sm text-gray-500 mt-1">Maximum points that can be earned in one transaction</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Point Expiry (Days)</label>
                <input
                  type="number"
                  defaultValue="365"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-sm text-gray-500 mt-1">Days after which points expire</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Welcome Bonus Points</label>
                <input
                  type="number"
                  defaultValue="50"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-sm text-gray-500 mt-1">Points given to new customers on signup</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Referral Bonus Points</label>
                <input
                  type="number"
                  defaultValue="100"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <p className="text-sm text-gray-500 mt-1">Points given for successful referrals</p>
              </div>
            </div>
            <div className="flex justify-end space-x-4">
              <button
                type="button"
                onClick={() => setShowRewardConfigModal(false)}
                className="px-6 py-3 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="submit"
                className={`${theme.button} px-6 py-3 rounded-lg`}
                onClick={(e) => {
                  e.preventDefault();
                  toast.success('Reward configuration updated successfully!');
                  setShowRewardConfigModal(false);
                }}
              >
                Save Configuration
              </button>
            </div>
          </form>
        </Modal>

        <Modal isOpen={showManageOffersModal} onClose={() => setShowManageOffersModal(false)} title="Manage Offers Analytics" size="xl">
          <div className="space-y-6">
            {/* Offer Analytics Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="text-left py-4 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('name')}
                    >
                      Offer Name {sortField === 'name' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-left py-4 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('coupon')}
                    >
                      Coupon Code {sortField === 'coupon' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-left py-4 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('views')}
                    >
                      Views {sortField === 'views' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-left py-4 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('used')}
                    >
                      Used {sortField === 'used' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700">Conversion</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700">Validity</th>
                    <th 
                      className="text-left py-4 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('status')}
                    >
                      Status {sortField === 'status' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const sortedOffers = sortData(currentOffers, sortField, sortDirection);
                    const paginatedOffers = paginateData(sortedOffers, currentPage, itemsPerPage);
                    
                    return paginatedOffers.map((offer, index) => (
                      <tr 
                        key={index} 
                        className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer"
                        onClick={() => handleOfferClick(offer)}
                      >
                        <td className="py-4 px-6 text-gray-900 font-medium">{offer.name}</td>
                        <td className="py-4 px-6 text-gray-600 font-mono">{offer.code || 'N/A'}</td>
                        <td className="py-4 px-6 text-blue-600 font-semibold">{offer.views || 0}</td>
                        <td className="py-4 px-6 text-green-600 font-semibold">{offer.used || 0}</td>
                        <td className="py-4 px-6 text-purple-600 font-semibold">{offer.conversion || '0%'}</td>
                        <td className="py-4 px-6 text-gray-500">{offer.validity || offer.expiry || 'No expiry'}</td>
                        <td className="py-4 px-6">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            offer.status === 'Active' || offer.active ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                          }`}>
                            {offer.status || (offer.active ? 'Active' : 'Inactive')}
                          </span>
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>

            {/* Pagination Controls */}
            <div className="flex items-center justify-between border-t pt-6">
              <p className="text-gray-600">
                Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, currentOffers.length)} of {currentOffers.length} offers
              </p>
              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                {[...Array(Math.ceil(currentOffers.length / itemsPerPage))].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-4 py-2 rounded-lg ${
                      currentPage === i + 1 
                        ? 'bg-blue-600 text-white' 
                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(currentOffers.length / itemsPerPage)))}
                  disabled={currentPage === Math.ceil(currentOffers.length / itemsPerPage)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>

          

            <div className="flex justify-end">
              <button
                onClick={() => setShowManageOffersModal(false)}
                className={`${theme.button} px-6 py-3 rounded-lg`}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>

        {/* <Modal isOpen={showCustomerManagementModal} onClose={() => setShowCustomerManagementModal(false)} title="Customer Management" size="xl">
          <div className="text-center py-8">
            <Users className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Customer Management</h3>
            <p className="text-gray-600 mb-6">Customer management tools are coming soon.</p>
            <button
              onClick={() => setShowCustomerManagementModal(false)}
              className={`${theme.button} px-6 py-2 rounded-lg`}
            >
              Close
            </button>
          </div>
        </Modal> */}

        {/* <Modal isOpen={showBusinessAnalyticsModal} onClose={() => setShowBusinessAnalyticsModal(false)} title="Business Analytics" size="xl">
          <div className="text-center py-8">
            <BarChart3 className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-gray-900 mb-2">Business Analytics</h3>
            <p className="text-gray-600 mb-6">Advanced analytics dashboard is coming soon.</p>
            <button
              onClick={() => setShowBusinessAnalyticsModal(false)}
              className={`${theme.button} px-6 py-2 rounded-lg`}
            >
              Close
            </button>
          </div>
        </Modal> */}

        <Modal isOpen={showViewAllActivityModal} onClose={() => setShowViewAllActivityModal(false)} title="All Business Activity" size="xl">
          <div className="space-y-6">
            {/* Activity Filters */}
            <div className="flex flex-col lg:flex-row gap-4">
              <div className="flex-1">
                <input
                  type="text"
                  placeholder="Search by customer name, email, or activity..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </div>
              <div className="lg:w-48">
                <select
                  value={filterType}
                  onChange={(e) => setFilterType(e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                >
                  <option value="all">All Activities</option>
                  <option value="earning">Points Earned</option>
                  <option value="redemption">Points Redeemed</option>
                  <option value="purchase">Purchases</option>
                  <option value="offer">Offer Usage</option>
                </select>
              </div>
            </div>

            {/* Activity Table */}
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50">
                  <tr>
                    <th 
                      className="text-left py-4 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('customer')}
                    >
                      Customer {sortField === 'customer' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700">Redeemed Points</th>
                    <th 
                      className="text-right py-4 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('amount')}
                    >
                       Purchase Amount {sortField === 'amount' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th 
                      className="text-left py-4 px-6 font-semibold text-gray-700 cursor-pointer hover:bg-gray-100"
                      onClick={() => handleSort('type')}
                    >
                      Type {sortField === 'type' && (sortDirection === 'asc' ? '↑' : '↓')}
                    </th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700">Date & Time</th>
                    <th className="text-left py-4 px-6 font-semibold text-gray-700">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    const allActivities = [
                      ...recentActivity,
                      { id: 6, customer: 'Anita Sharma', email: 'anita@email.com', action: 'Redeemed 200 points', amount: '₹200 discount', time: '2 hours ago', type: 'redemption' },
                      { id: 7, customer: 'Karan Singh', email: 'karan@email.com', action: 'Used SUMMER20 coupon', amount: '₹1,800', time: '3 hours ago', type: 'offer' },
                      { id: 8, customer: 'Meera Patel', email: 'meera@email.com', action: 'Earned 95 points', amount: '₹950', time: '4 hours ago', type: 'earning' },
                      { id: 9, customer: 'Rohit Kumar', email: 'rohit@email.com', action: 'Made purchase', amount: '₹2,300', time: '5 hours ago', type: 'purchase' },
                      { id: 10, customer: 'Sita Devi', email: 'sita@email.com', action: 'Earned 150 points', amount: '₹1,500', time: '6 hours ago', type: 'earning' }
                    ];

                    const filteredActivities = allActivities.filter(activity => {
                      const matchesSearch = activity.customer.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                          activity.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
                                          activity.action.toLowerCase().includes(searchTerm.toLowerCase());
                      const matchesFilter = filterType === 'all' || activity.type === filterType;
                      return matchesSearch && matchesFilter;
                    });

                    const sortedActivities = sortData(filteredActivities, sortField, sortDirection);
                    const paginatedActivities = paginateData(sortedActivities, currentPage, itemsPerPage);

                    return paginatedActivities.map((activity) => (
                      <tr key={activity.id} className="border-b border-gray-100 hover:bg-gray-50">
                        <td className="py-4 px-6">
                          <div>
                            <p className="font-medium text-gray-900">{activity.customer}</p>
                            <p className="text-sm text-gray-500">{activity.email}</p>
                          </div>
                        </td>
                        <td className=" text-right py-4 px-6 text-gray-700">{activity.action}</td>
                        <td className=" text-right py-4 px-6 font-semibold text-gray-900">{activity.amount}</td>
                        <td className="py-4 px-6">
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            activity.type === 'earning' ? 'bg-green-100 text-green-800' :
                            activity.type === 'redemption' ? 'bg-blue-100 text-blue-800' :
                            activity.type === 'purchase' ? 'bg-purple-100 text-purple-800' :
                            'bg-orange-100 text-orange-800'
                          }`}>
                            {activity.type.charAt(0).toUpperCase() + activity.type.slice(1)}
                          </span>
                        </td>
                        <td className="py-4 px-6 text-gray-500">{activity.time}</td>
                        <td className="py-4 px-6">
                          <span className="px-3 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                            Completed
                          </span>
                        </td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="flex items-center justify-between border-t pt-6">
              <p className="text-gray-600">
                Showing {((currentPage - 1) * itemsPerPage) + 1}-{Math.min(currentPage * itemsPerPage, 10)} of 10+ activities
              </p>
              <div className="flex items-center space-x-2">
                <button 
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Previous
                </button>
                {[...Array(Math.ceil(10 / itemsPerPage))].map((_, i) => (
                  <button
                    key={i + 1}
                    onClick={() => setCurrentPage(i + 1)}
                    className={`px-4 py-2 rounded-lg ${
                      currentPage === i + 1 
                        ? 'bg-blue-600 text-white' 
                        : 'border border-gray-300 text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    {i + 1}
                  </button>
                ))}
                <button 
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, Math.ceil(10 / itemsPerPage)))}
                  disabled={currentPage === Math.ceil(10 / itemsPerPage)}
                  className="px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                >
                  Next
                </button>
              </div>
            </div>

            <div className="flex justify-end">
              <button
                onClick={() => setShowViewAllActivityModal(false)}
                className={`${theme.button} px-6 py-3 rounded-lg`}
              >
                Close
              </button>
            </div>
          </div>
        </Modal>

        {/* ATM Receipt Style Modal for Individual Offer Details */}
        <Modal isOpen={showOfferReceiptModal} onClose={() => setShowOfferReceiptModal(false)} title="Offer Analytics Receipt" size="lg">
          {selectedOffer && (
            <div className="bg-white">
              {/* Receipt Header */}
              <div className="text-center border-b-2 border-dashed border-gray-300 pb-4 mb-6">
                <h2 className="text-2xl font-bold text-gray-900 mb-2">OFFER ANALYTICS</h2>
                <p className="text-gray-600">Transaction Receipt</p>
                <p className="text-sm text-gray-500">Generated: {new Date().toLocaleString()}</p>
              </div>

              {/* Offer Details */}
              <div className="space-y-4 mb-6">
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-700">Offer Name:</span>
                  <span className="font-bold text-gray-900">{selectedOffer.name}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-700">Coupon Code:</span>
                  <span className="font-mono font-bold text-blue-600">{selectedOffer.coupon}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-700">Description:</span>
                  <span className="text-gray-900">{selectedOffer.description}</span>
                </div>
                <div className="flex justify-between items-center">
                  <span className="font-medium text-gray-700">Status:</span>
                  <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                    selectedOffer.status === 'Active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                  }`}>
                    {selectedOffer.status}
                  </span>
                </div>
              </div>

              {/* Analytics Section */}
              <div className="border-t-2 border-dashed border-gray-300 pt-4 mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">PERFORMANCE METRICS</h3>
                <div className="grid grid-cols-2 gap-4">
                  <div className="text-center p-4 bg-blue-50 rounded-lg">
                    <p className="text-sm text-blue-600 font-medium">TOTAL VIEWS</p>
                    <p className="text-2xl font-bold text-blue-900">{selectedOffer.views.toLocaleString()}</p>
                  </div>
                  <div className="text-center p-4 bg-green-50 rounded-lg">
                    <p className="text-sm text-green-600 font-medium">MEMBERS USED</p>
                    <p className="text-2xl font-bold text-green-900">{selectedOffer.used}</p>
                  </div>
                  <div className="text-center p-4 bg-purple-50 rounded-lg">
                    <p className="text-sm text-purple-600 font-medium">CONVERSION RATE</p>
                    <p className="text-2xl font-bold text-purple-900">{selectedOffer.conversion}</p>
                  </div>
                  <div className="text-center p-4 bg-orange-50 rounded-lg">
                    <p className="text-sm text-orange-600 font-medium">REVENUE</p>
                    <p className="text-2xl font-bold text-orange-900">{selectedOffer.purchases}</p>
                  </div>
                </div>
              </div>

              {/* Purchase Details */}
              <div className="border-t-2 border-dashed border-gray-300 pt-4 mb-6">
                <h3 className="text-lg font-bold text-gray-900 mb-4 text-center">PURCHASE DETAILS</h3>
                <div className="space-y-3">
                  <div className="flex justify-between">
                    <span className="text-gray-700">Start Date:</span>
                    <span className="font-medium">{selectedOffer.startDate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">End Date:</span>
                    <span className="font-medium">{selectedOffer.endDate}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Min Purchase:</span>
                    <span className="font-medium">{selectedOffer.minPurchase}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Max Discount:</span>
                    <span className="font-medium">{selectedOffer.maxDiscount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-700">Validity:</span>
                    <span className="font-medium">{selectedOffer.validity}</span>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="border-t-2 border-dashed border-gray-300 pt-4 text-center">
                <p className="text-sm text-gray-500 mb-2">Thank you for using our analytics system</p>
                <p className="text-xs text-gray-400">This receipt contains confidential business information</p>
              </div>

              {/* Close Button */}
              <div className="mt-6 text-center">
                <button
                  onClick={() => setShowOfferReceiptModal(false)}
                  className={`${theme.button} px-8 py-3 rounded-lg`}
                >
                  Close Receipt
                </button>
              </div>
            </div>
          )}
        </Modal>

        {/* Buy Points Modal */}
       <Modal isOpen={showBuyPointsModal} onClose={() => setShowBuyPointsModal(false)}>
      <div className="bg-white rounded-xl p-8 max-w-md w-full mx-4">
        <div className="text-center mb-6">
          <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <CreditCard className="w-8 h-8 text-blue-600" />
          </div>
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Buy Points</h2>
          <p className="text-gray-600">Purchase points from admin to reward your customers</p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Number of Points
            </label>
            <input
              type="number"
              name="points"
              min="100"
              step="100"
              required
              value={points}
              onChange={handlePointsChange}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              placeholder="Enter points (min 100)"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Amount (₹)
            </label>
            <input
              type="text"
              name="amount"
              value={amount ? `₹${amount}` : ''}
              readOnly
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-100"
              placeholder="Amount will be calculated automatically"
            />
          </div>

          <button
            type="submit"
            className="px-4 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium w-full"
          >
            Send Request
          </button>
        </form>
      </div>
    </Modal>

        {/* QR Scanner Modal */}
        {showQRScanner && (
          <QRScanner
            onClose={() => setShowQRScanner(false)}
            onScan={handleQRScan}
          />
        )}

        {/* QR Generator Modal */}
        {showQRGenerator && (
          <QRGenerator
            onClose={() => setShowQRGenerator(false)}
            shopData={profileData}
          />
        )}



        {/* Manage Offers Page */}
        {showManageOffers && (
          <ManageOffers
            onBack={() => setShowManageOffers(false)}
          />
        )}
      </div>
    </div>
  );
};

export default BusinessDashboard;
